import React, { useState, useEffect, useMemo } from 'react';

// All 50 photo filenames (with sanitized names)
const PHOTO_FILES_BASE = [
    '00398-01_1200.jpg',
    '01216-04_1200.jpg',
    '01216-07_1200.jpg',
    '01545-03.jpg',
    '01545-04.jpg',
    '0e08fe036bbea11190d54a9adbc7ebbe.1.jpg',
    '1.jpg',
    '10122083_preview.jpg',
    '11697529.jpg',
    '12.jpg',
    '1721887075_screenshot_24.jpg',
    '1767179476_screenshot_91.jpg',
    '1__1_.jpg',
    '1__2_.jpg',
    '202913549269959578_9999.jpg',
    '291290372696041982_9999.jpg',
    '2a7f3aa4709da0c8e356f6c5b53d44ca.2.jpg',
    '36182-03_1800.jpg',
    '4971546_00360_1280c.jpg',
    '505-set-3x.jpg',
    '6.jpg',
    '6__1_.jpg',
    '7829e99c74657028046f71ac9e022108.9.jpg',
    '783234-b23832790c8480776575bee48a15e236-post_single_big.jpg',
    '8.jpg',
    '8001134.jpg',
    '81a2aa4ad7df031250918b595c309e0d.6.jpg',
    '8240499.jpg',
    '866590541127537105_9999.jpg',
    '90405697bb5f3f560dfadc0c57e0217f.5.jpg',
    '9893-3x.jpg',
    'Kalani-Luana.jpg',
    'MG_38671.jpg',
    'Model_Thumb-1.jpg',
    'Provocative-Kalani-Luana-drops-on-her-knees-for-a-facial-Exxxtra-Small_002.jpg',
    '_m_eaSaaTbaAaaaa__mh_8OshZ896KexejQLv_5.jpg',
    'a866fc4a-5920-453f-a79c-af9548c82cdc_profile-1.webp',
    'cb7f291b460eb4b1550234bdbde5daee.15.jpg',
    'cb7f291b460eb4b1550234bdbde5daee.5.jpg',
    'deeplush-kalani_luana-1090338.jpg',
    'f53c7452f610df1f88d22a1869087684.8.jpg',
    'hq720.jpg',
    'images.jpg',
    'kalani-luana-1639915605_large.jpg',
    'kalani-luana-955263594_large.jpg',
    'maxresdefault.jpg',
    'preview_720p.mp4.jpg',
    'preview__1_.jpg',
    'preview__2_.jpg',
    'preview__3_.jpg'
];

// Interleave duplicates to ensure same photos are spread apart
const interleavePhotos = (baseArray, copies) => {
    const result = [];
    const shuffledSets = [];
    
    for (let c = 0; c < copies; c++) {
        const shuffled = [...baseArray];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(((i * (c + 1) * 7) % (i + 1)));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        shuffledSets.push(shuffled);
    }
    
    const maxLen = baseArray.length;
    for (let i = 0; i < maxLen; i++) {
        for (let c = 0; c < copies; c++) {
            result.push(shuffledSets[c][i]);
        }
    }
    
    return result;
};

const PHOTO_FILES = interleavePhotos(PHOTO_FILES_BASE, 2);

const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

const generatePhotoStyle = (index, total) => {
    const gridCols = 8;
    const gridRows = 5;
    const col = index % gridCols;
    const row = Math.floor((index / gridCols) % gridRows);
    
    const cellWidth = 100 / (gridCols - 2);
    const cellHeight = 100 / (gridRows - 1);
    
    const baseX = (col - 1) * cellWidth + (Math.random() - 0.5) * cellWidth * 1.8;
    const baseY = (row - 0.5) * cellHeight + (Math.random() - 0.5) * cellHeight * 1.4;
    
    const clampedX = Math.max(-20, Math.min(120, baseX));
    const clampedY = Math.max(-15, Math.min(115, baseY));
    
    const rotation = (Math.random() - 0.5) * 40;
    const startRotation = rotation + (Math.random() > 0.5 ? 1 : -1) * (15 + Math.random() * 20);
    
    const maxWidth = 250 + Math.random() * 150;
    
    return {
        left: `${clampedX}%`,
        top: `${clampedY}%`,
        maxWidth: `${maxWidth}px`,
        '--start-rotation': `${startRotation}deg`,
        '--end-rotation': `${rotation}deg`,
        zIndex: index + 1,
    };
};

const MagazinePhoto = ({ src, index, total, isVisible, delay }) => {
    const style = useMemo(() => generatePhotoStyle(index, total), [index, total]);
    
    return (
        <div
            className="magazine-photo"
            style={{
                ...style,
                opacity: isVisible ? 1 : 0,
                animationName: isVisible ? 'photoStack' : 'none',
                animationDuration: '0.4s',
                animationTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                animationDelay: `${delay}ms`,
                animationFillMode: 'forwards',
            }}
        >
            <img
                src={`/site-photos/${src}`}
                alt=""
                loading="eager"
                draggable="false"
            />
        </div>
    );
};

const FlashEffect = ({ trigger }) => {
    const [show, setShow] = useState(false);
    
    useEffect(() => {
        if (trigger) {
            setShow(true);
            const timer = setTimeout(() => setShow(false), 150);
            return () => clearTimeout(timer);
        }
    }, [trigger]);
    
    if (!show) return null;
    
    return <div className="flash-overlay" />;
};

export const MagazineStackLoader = ({ onComplete }) => {
    const [visibleCount, setVisibleCount] = useState(0);
    const [flashTrigger, setFlashTrigger] = useState(0);
    const [isComplete, setIsComplete] = useState(false);
    
    const shuffledPhotos = useMemo(() => shuffleArray(PHOTO_FILES), []);
    
    const baseDelay = 70;
    const flashInterval = 8;
    
    useEffect(() => {
        if (visibleCount >= shuffledPhotos.length) {
            setIsComplete(true);
            if (onComplete) {
                setTimeout(onComplete, 500);
            }
            return;
        }
        
        if (visibleCount > 0 && visibleCount % flashInterval === 0) {
            setFlashTrigger(prev => prev + 1);
        }
        
        const burstMode = Math.random() > 0.7;
        const variation = (Math.random() - 0.5) * 50;
        const delay = burstMode ? 50 + variation * 0.3 : baseDelay + variation;
        
        const timer = setTimeout(() => {
            setVisibleCount(prev => prev + 1);
        }, delay);
        
        return () => clearTimeout(timer);
    }, [visibleCount, shuffledPhotos.length, onComplete]);
    
    return (
        <div className="fixed inset-0 bg-charcoal overflow-hidden">
            <div 
                className="absolute inset-0"
                style={{
                    background: 'radial-gradient(ellipse at 30% 20%, hsl(330 85% 15% / 0.3) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, hsl(200 100% 15% / 0.2) 0%, transparent 50%), #1a1a1a'
                }}
            />
            
            <div className="relative w-full h-full">
                {shuffledPhotos.map((photo, index) => (
                    <MagazinePhoto
                        key={`${photo}-${index}`}
                        src={photo}
                        index={index}
                        total={shuffledPhotos.length}
                        isVisible={index < visibleCount}
                        delay={0}
                    />
                ))}
            </div>
            
            <FlashEffect trigger={flashTrigger} />
            <div className="magazine-grain-overlay" />
            <div className="scanlines" />
            <div className="vignette" />
        </div>
    );
};

export default MagazineStackLoader;
